import { type FC } from 'react'

import { Call } from '@liveHelper/assets/Call'
import { CamOff } from '@liveHelper/assets/CamOff'
import { ChatFilled } from '@liveHelper/assets/ChatFilled'
import {
  contentSection,
  ctaContainer,
  menuCoverImage,
  mutedText,
  textChatLink,
} from '@liveHelper/components/Idle/Idle.twind'
import {
  expandedHeaderDescription,
  expandedHeaderImageContainer,
  expandedHeaderTitle,
} from '@liveHelper/components/shared/shared.twind'
import {
  CTACloseButton,
  FireworkLogoWrapper,
  IdleExpandedContainer,
} from '@liveHelper/components/shared/styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useChatOnly } from '@liveHelper/hooks/useChatOnly'
import { useInitiateCall } from '@liveHelper/hooks/useInitiateCall'
import {
  actionsSelector,
  paramsSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_BASE_CONTAINER } from '@liveHelper/testIds'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import { useTrackClickStartChat } from '@liveHelper/tracking/useLiveHelperTracking'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding/FireworkBranding'

import { Close } from '@core/assets/Close'
import { Button } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

/**
 * Expanded state of the idle widget.
 */
export const IdleExpanded: FC = () => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const { lang, widget_id } = useStore(paramsSelector)
  const { callAgent, chatWithAgent } = useStore(visitorPrivateActionsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const chatChannelConfig = useChatChannelConfig()
  const utmContent = useGetUtmContent()

  const trackClickStartChat = useTrackClickStartChat()

  const initiateCall = useInitiateCall({ shouldTrackClickStartCall: true })

  const [chatOnly] = useChatOnly()

  return (
    <IdleExpandedContainer
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      isMobileWebEnabled={isMobileWeb()}
    >
      <CTACloseButton
        shape="circle"
        aria-label={t(`oneToOne:End Conversation`)}
        background="var(--gray-9)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--white)" label="" aria-hidden />}
      />
      <div className={tw?.(expandedHeaderImageContainer, 'overflow-hidden')}>
        <div
          className={tw?.(
            menuCoverImage(
              chatChannelConfig?.widget_style?.menu_cover_image_url,
            ),
          )}
        ></div>
      </div>
      <div className={tw?.(contentSection, 'px-10', 'pt-10', 'pb-7')}>
        <div className={tw?.('flex', 'flex-col', 'items-center', 'gap-2.5')}>
          <div
            className={tw?.(
              expandedHeaderTitle,
              'text-lg',
              'font-semibold',
              'text-center',
            )}
          >
            {chatChannelConfig?.widget_style?.expanded_state_text ||
              t(`oneToOne:Let's find your product`)}
          </div>
          <div
            className={tw?.(
              expandedHeaderDescription,
              'text-sm',
              'text-center',
              'max-w-full',
              'break-words',
            )}
          >
            {chatChannelConfig?.widget_style?.widget_description_text ||
              t(
                `oneToOne:A product expert will assist you live over video and answer your question.`,
              )}
          </div>
          {chatOnly ? null : (
            <div className={tw?.('flex', 'gap-2', 'items-center')}>
              <CamOff width={'14px'} fill="var(--gray-7)" label="cam off" />
              <span className={tw?.(mutedText, 'text-xs')}>
                {t(`oneToOne:You won't appear on camera`)}
              </span>
            </div>
          )}
        </div>
        <div
          className={tw?.(
            ctaContainer,
            'flex',
            'flex-col',
            'gap-2',
            'mt-10',
            'mb-5',
            'mx-4',
          )}
        >
          {chatOnly ? (
            <Button
              aria-label={t(`oneToOne:Start chat`)}
              disabled={!chatWithAgent}
              onClick={async () => {
                await chatWithAgent?.()
                trackClickStartChat()
                customLiveHelperEvent({
                  type: EventType.FW_LIVE_HELPER_CLICK_CHAT_WITH_AGENT,
                  data: {
                    widget_id,
                  },
                })
              }}
            >
              <div className={tw?.('flex', 'gap-2', 'items-center')}>
                <ChatFilled
                  fill="var(--white)"
                  label=""
                  width="16px"
                  height="16px"
                  aria-hidden
                />
                {t(`oneToOne:Start chat`)}
              </div>
            </Button>
          ) : (
            <>
              <Button
                aria-label={t(`oneToOne:Shop Live`)}
                disabled={!callAgent}
                onClick={() => initiateCall()}
              >
                <div className={tw?.('flex', 'gap-2', 'items-center')}>
                  <Call
                    width="16px"
                    height="16px"
                    fill="var(--white)"
                    label=""
                    aria-hidden
                  />
                  {t(`oneToOne:Shop Live`)}
                </div>
              </Button>
              <Button
                className={tw?.(textChatLink)}
                variant="outlined"
                aria-label={t(`oneToOne:Use text instead`)}
                disabled={!chatWithAgent}
                onClick={async () => {
                  await chatWithAgent?.()
                  trackClickStartChat()
                  customLiveHelperEvent({
                    type: EventType.FW_LIVE_HELPER_CLICK_CHAT_WITH_AGENT,
                    data: {
                      widget_id,
                    },
                  })
                }}
              >
                {t('oneToOne:Use text instead')}
              </Button>
            </>
          )}
        </div>
      </div>
      {chatChannelConfig?.widget_style?.firework_branding ? (
        <FireworkLogoWrapper>
          <FireworkBranding dark utmContent={utmContent} lang={lang} />
        </FireworkLogoWrapper>
      ) : null}
    </IdleExpandedContainer>
  )
}
